import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { ERPPage, ERPPageHeader, ERPButton } from '../../components/ui/ERPLayout';
import unifiedClientService from '../../services/unifiedClientService';
import CondicionesCortePanel from '../client-management/components/CondicionesCortePanel';

// ─── TAB DEFINITIONS ──────────────────────────────────────────────────────────
// NOTE: 'facturacion' tab is intentionally excluded — no real data source exists yet.
const TABS = [
  { id: 'general',           label: 'Datos generales',      icon: 'User' },
  { id: 'condiciones_corte', label: 'Condiciones de Corte', icon: 'Scissors' },
  { id: 'historial',         label: 'Historial',            icon: 'History' },
  { id: 'rentabilidad',      label: 'Rentabilidad',         icon: 'TrendingUp' },
];

// ─── FIELD COMPONENTS ─────────────────────────────────────────────────────────
const FieldGroup = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-oranienbaum text-xs font-semibold text-white/40 uppercase tracking-widest mb-3 pb-2 border-b border-white/8">
      {title}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

const Field = ({ label, value, fullWidth = false }) => (
  <div className={fullWidth ? 'md:col-span-2 xl:col-span-3' : ''}>
    <p className="font-oranienbaum text-xs text-white/40 mb-1">{label}</p>
    <p className="font-oranienbaum text-sm text-white/85">
      {value !== undefined && value !== null && value !== '' ? String(value) : <span className="text-white/25 italic">—</span>}
    </p>
  </div>
);

const EditableField = ({ label, name, value, onChange, type = 'text', placeholder = '', fullWidth = false, disabled = false }) => (
  <div className={fullWidth ? 'md:col-span-2 xl:col-span-3' : ''}>
    <label className="block font-oranienbaum text-xs text-white/40 mb-1">{label}</label>
    {type === 'textarea' ? (
      <textarea
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 font-oranienbaum placeholder-white/20 focus:outline-none focus:border-[#D6EF4E]/50 resize-none disabled:opacity-40"
      />
    ) : type === 'checkbox' ? (
      <div className="flex items-center gap-2 mt-1">
        <input
          type="checkbox"
          name={name}
          checked={!!value}
          onChange={onChange}
          disabled={disabled}
          className="w-4 h-4 rounded border-white/20 bg-[#141414] text-[#D6EF4E] focus:ring-[#D6EF4E]/30"
        />
        <span className="font-oranienbaum text-sm text-white/60">{placeholder}</span>
      </div>
    ) : (
      <input
        type={type}
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 font-oranienbaum placeholder-white/20 focus:outline-none focus:border-[#D6EF4E]/50 disabled:opacity-40"
      />
    )}
  </div>
);

// ─── TAB: DATOS GENERALES ─────────────────────────────────────────────────────
const TabGeneral = ({ client, editing, formData, onChange }) => {
  if (!editing) {
    return (
      <>
        <FieldGroup title="Identificación">
          <Field label="Nombre" value={client?.nombre} />
          <Field label="Nombre comercial" value={client?.nombreComercial} />
          <Field label="NIF/CIF" value={client?.nifCif} />
          <Field label="Contacto principal" value={client?.contactoPrincipal} />
          <Field label="Teléfono" value={client?.telefono} />
          <Field label="Email" value={client?.email} />
        </FieldGroup>
        <FieldGroup title="Dirección">
          <Field label="Dirección" value={client?.direccion} fullWidth />
          <Field label="Población" value={client?.poblacion} />
          <Field label="Provincia" value={client?.provincia} />
          <Field label="Código postal" value={client?.codigoPostal} />
        </FieldGroup>
        <FieldGroup title="Clasificación">
          <Field label="Categoría" value={client?.categoria} />
          <div>
            <p className="font-oranienbaum text-xs text-white/40 mb-2">Tipo de cliente</p>
            <div className="flex flex-wrap gap-2">
              {client?.clienteSalaCorte && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-oranienbaum font-semibold bg-[#C9A24D]/15 text-[#C9A24D] border border-[#C9A24D]/30">
                  ✂️ Sala de Corte
                </span>
              )}
              {client?.clienteDistribucion && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-oranienbaum font-semibold bg-[#8097D6]/15 text-[#8097D6] border border-[#8097D6]/30">
                  🚚 Distribución
                </span>
              )}
              {!client?.clienteSalaCorte && !client?.clienteDistribucion && (
                <span className="text-white/30 text-xs font-oranienbaum italic">Sin clasificación</span>
              )}
            </div>
          </div>
        </FieldGroup>
        <FieldGroup title="Tarifas de loncheado">
          <Field label="Tarifa sin emplatado (menor)" value={client?.tarifaSinEmplatadoMenor ? `${client?.tarifaSinEmplatadoMenor} €/kg` : null} />
          <Field label="Tarifa sin emplatado (mayor)" value={client?.tarifaSinEmplatadoMayor ? `${client?.tarifaSinEmplatadoMayor} €/kg` : null} />
          <Field label="Tarifa emplatado" value={client?.tarifaEmplatado ? `${client?.tarifaEmplatado} €/kg` : null} />
        </FieldGroup>
      </>
    );
  }

  return (
    <>
      <FieldGroup title="Identificación">
        <EditableField label="Nombre *" name="nombre" value={formData?.nombre} onChange={onChange} placeholder="Nombre del cliente" />
        <EditableField label="Nombre comercial" name="nombreComercial" value={formData?.nombreComercial} onChange={onChange} placeholder="Nombre comercial" />
        <EditableField label="NIF/CIF" name="nifCif" value={formData?.nifCif} onChange={onChange} placeholder="B12345678" />
        <EditableField label="Contacto principal" name="contactoPrincipal" value={formData?.contactoPrincipal} onChange={onChange} placeholder="Nombre del contacto" />
        <EditableField label="Teléfono" name="telefono" value={formData?.telefono} onChange={onChange} placeholder="+34 600 000 000" type="tel" />
        <EditableField label="Email" name="email" value={formData?.email} onChange={onChange} placeholder="cliente@empresa.com" type="email" />
      </FieldGroup>
      <FieldGroup title="Dirección">
        <EditableField label="Dirección" name="direccion" value={formData?.direccion} onChange={onChange} placeholder="Calle, número, piso..." fullWidth />
        <EditableField label="Población" name="poblacion" value={formData?.poblacion} onChange={onChange} placeholder="Ciudad" />
        <EditableField label="Provincia" name="provincia" value={formData?.provincia} onChange={onChange} placeholder="Provincia" />
        <EditableField label="Código postal" name="codigoPostal" value={formData?.codigoPostal} onChange={onChange} placeholder="28001" />
      </FieldGroup>
      <FieldGroup title="Clasificación">
        <div>
          <label className="block font-oranienbaum text-xs text-white/40 mb-1">Categoría</label>
          <select
            name="categoria"
            value={formData?.categoria || ''}
            onChange={onChange}
            className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/50"
          >
            <option value="">Sin categoría</option>
            <option value="minorista">Minorista</option>
            <option value="tienda">Tienda</option>
            <option value="bar">Bar</option>
            <option value="restaurante">Restaurante</option>
          </select>
        </div>
        <div>
          <p className="font-oranienbaum text-xs text-white/40 mb-2">Tipo de cliente</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="clienteSalaCorte"
                checked={!!formData?.clienteSalaCorte}
                onChange={onChange}
                className="w-4 h-4 rounded border-white/20 bg-[#141414] accent-[#C9A24D]"
              />
              <span className="font-oranienbaum text-sm text-[#C9A24D]">✂️ Cliente Sala de Corte</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="clienteDistribucion"
                checked={!!formData?.clienteDistribucion}
                onChange={onChange}
                className="w-4 h-4 rounded border-white/20 bg-[#141414] accent-[#8097D6]"
              />
              <span className="font-oranienbaum text-sm text-[#8097D6]">🚚 Cliente Distribución</span>
            </label>
          </div>
        </div>
      </FieldGroup>
      <FieldGroup title="Tarifas de loncheado">
        <EditableField label="Tarifa sin emplatado menor (€/kg)" name="tarifaSinEmplatadoMenor" value={formData?.tarifaSinEmplatadoMenor} onChange={onChange} type="number" placeholder="0.00" />
        <EditableField label="Tarifa sin emplatado mayor (€/kg)" name="tarifaSinEmplatadoMayor" value={formData?.tarifaSinEmplatadoMayor} onChange={onChange} type="number" placeholder="0.00" />
        <EditableField label="Tarifa emplatado (€/kg)" name="tarifaEmplatado" value={formData?.tarifaEmplatado} onChange={onChange} type="number" placeholder="0.00" />
      </FieldGroup>
    </>
  );
};

// ─── TAB: HISTORIAL ───────────────────────────────────────────────────────────
const TabHistorial = ({ loncheados, loadingHistory }) => {
  if (loadingHistory) {
    return (
      <div className="py-16 flex justify-center">
        <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
      </div>
    );
  }

  if (!loncheados?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#8097D6]/10 flex items-center justify-center mb-4">
          <Icon name="History" size={28} color="#8097D6" />
        </div>
        <h3 className="font-oranienbaum font-semibold text-white/70 text-lg mb-2">Sin historial</h3>
        <p className="font-oranienbaum text-white/35 text-sm">No hay loncheados registrados para este cliente.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="font-oranienbaum text-xs text-white/40 mb-4">{loncheados?.length} registro{loncheados?.length !== 1 ? 's' : ''} encontrado{loncheados?.length !== 1 ? 's' : ''}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-oranienbaum">
          <thead>
            <tr className="border-b border-white/8">
              <th className="text-left py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Fecha</th>
              <th className="text-left py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Producto</th>
              <th className="text-left py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Empleado</th>
              <th className="text-right py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Peso inicial</th>
              <th className="text-right py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Aprovechado</th>
              <th className="text-right py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Merma</th>
              <th className="text-right py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Rendimiento</th>
              <th className="text-left py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wide">Tipo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loncheados?.map((item) => {
              const fecha = item?.fecha_produccion || item?.created_at;
              const rendimiento = item?.rendimiento ? Number(item?.rendimiento)?.toFixed(1) : null;
              return (
                <tr key={item?.id} className="hover:bg-white/3 transition-colors">
                  <td className="py-2 px-3 text-white/55 whitespace-nowrap">
                    {fecha ? new Date(fecha)?.toLocaleDateString('es-ES') : '—'}
                  </td>
                  <td className="py-2 px-3 text-white/80">{item?.producto?.nombre || '—'}</td>
                  <td className="py-2 px-3 text-white/60">{item?.empleado?.nombre || '—'}</td>
                  <td className="py-2 px-3 text-right text-white/60">
                    {item?.peso_inicial ? `${Number(item?.peso_inicial)?.toFixed(2)} kg` : '—'}
                  </td>
                  <td className="py-2 px-3 text-right text-[#3A7F6B]">
                    {item?.peso_aprovechado ? `${Number(item?.peso_aprovechado)?.toFixed(2)} kg` : '—'}
                  </td>
                  <td className="py-2 px-3 text-right text-[#E05252]">
                    {item?.merma ? `${Number(item?.merma)?.toFixed(2)} kg` : '—'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {rendimiento ? (
                      <span className={`font-semibold ${Number(rendimiento) >= 70 ? 'text-[#D6EF4E]' : Number(rendimiento) >= 60 ? 'text-[#C9A24D]' : 'text-[#E05252]'}`}>
                        {rendimiento}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-3 text-white/50 text-xs">
                    {item?.tipo || (item?.emplatado ? 'EMPLATADO' : 'NORMAL')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── TAB: RENTABILIDAD ────────────────────────────────────────────────────────
const TabRentabilidad = ({ loncheados, loadingRent, rentFilter, setRentFilter }) => {
  if (loadingRent) {
    return (
      <div className="py-16 flex justify-center">
        <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
      </div>
    );
  }

  if (!loncheados?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#3A7F6B]/10 flex items-center justify-center mb-4">
          <Icon name="TrendingUp" size={28} color="#3A7F6B" />
        </div>
        <h3 className="font-oranienbaum font-semibold text-white/70 text-lg mb-2">Sin datos de rentabilidad</h3>
        <p className="font-oranienbaum text-white/35 text-sm">No hay loncheados registrados para calcular la rentabilidad.</p>
      </div>
    );
  }

  const totalPiezas = loncheados?.length;
  const kgIniciales = loncheados?.reduce((s, l) => s + (Number(l?.peso_inicial) || 0), 0);
  const kgAprovechados = loncheados?.reduce((s, l) => s + (Number(l?.peso_aprovechado) || 0), 0);
  const mermaTotal = loncheados?.reduce((s, l) => s + (Number(l?.merma) || 0), 0);
  const rendMedio = kgIniciales > 0 ? (kgAprovechados / kgIniciales) * 100 : 0;

  const rentRows = loncheados?.flatMap(l => l?.rentabilidad || []);
  const ingresos = rentRows?.reduce((s, r) => s + (Number(r?.ingresos_totales) || 0), 0);
  const costeSalario = rentRows?.reduce((s, r) => s + (Number(r?.coste_salario) || 0), 0);
  const costePlasticos = rentRows?.reduce((s, r) => s + (Number(r?.coste_plasticos_total) || 0), 0);
  const costeTotal = rentRows?.reduce((s, r) => s + (Number(r?.coste_total) || 0), 0);
  const beneficio = rentRows?.reduce((s, r) => s + (Number(r?.beneficio) || 0), 0);
  const margen = ingresos > 0 ? (beneficio / ingresos) * 100 : 0;
  const hasRentData = rentRows?.length > 0;

  const KPICard = ({ label, value, color = 'text-white/80', sub = null }) => (
    <div className="bg-[#141414] rounded-xl border border-white/8 p-4">
      <p className="font-oranienbaum text-xs text-white/40 mb-1">{label}</p>
      <p className={`font-oranienbaum text-xl font-bold ${color}`}>{value}</p>
      {sub && <p className="font-oranienbaum text-xs text-white/30 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {['semana', 'mes', 'año', 'todo']?.map(f => (
          <button
            key={f}
            onClick={() => setRentFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-medium transition-colors ${
              rentFilter === f ? 'bg-[#D6EF4E] text-[#111111]' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
            }`}
          >
            {f === 'todo' ? 'Todo' : f?.charAt(0)?.toUpperCase() + f?.slice(1)}
          </button>
        ))}
        <span className="font-oranienbaum text-xs text-white/30 ml-2">{totalPiezas} piezas en el período</span>
      </div>

      <div className="mb-6">
        <h3 className="font-oranienbaum text-xs font-semibold text-white/40 uppercase tracking-widest mb-3">Producción</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Piezas procesadas" value={totalPiezas} />
          <KPICard label="Kg iniciales" value={`${kgIniciales?.toFixed(1)} kg`} />
          <KPICard label="Kg aprovechados" value={`${kgAprovechados?.toFixed(1)} kg`} color="text-[#3A7F6B]" />
          <KPICard label="Merma total" value={`${mermaTotal?.toFixed(1)} kg`} color="text-[#E05252]" />
        </div>
        <div className="mt-3">
          <KPICard
            label="Rendimiento medio"
            value={`${rendMedio?.toFixed(1)}%`}
            color={rendMedio >= 70 ? 'text-[#D6EF4E]' : rendMedio >= 60 ? 'text-[#C9A24D]' : 'text-[#E05252]'}
            sub={`${kgAprovechados?.toFixed(1)} kg aprovechados de ${kgIniciales?.toFixed(1)} kg iniciales`}
          />
        </div>
      </div>

      {hasRentData ? (
        <div className="mb-6">
          <h3 className="font-oranienbaum text-xs font-semibold text-white/40 uppercase tracking-widest mb-3">Económico</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KPICard label="Ingresos" value={`${ingresos?.toFixed(2)} €`} color="text-[#3A7F6B]" />
            <KPICard label="Coste laboral" value={`${costeSalario?.toFixed(2)} €`} color="text-[#E05252]" />
            <KPICard label="Coste plásticos" value={`${costePlasticos?.toFixed(2)} €`} color="text-[#E05252]" />
            <KPICard label="Coste total" value={`${costeTotal?.toFixed(2)} €`} color="text-[#E05252]" />
            <KPICard label="Beneficio" value={`${beneficio?.toFixed(2)} €`} color={beneficio >= 0 ? 'text-[#D6EF4E]' : 'text-[#E05252]'} />
            <KPICard label="Margen %" value={`${margen?.toFixed(1)}%`} color={margen >= 20 ? 'text-[#D6EF4E]' : margen >= 10 ? 'text-[#C9A24D]' : 'text-[#E05252]'} />
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-[#C9A24D]/8 border border-[#C9A24D]/20 rounded-xl">
          <p className="font-oranienbaum text-sm text-[#C9A24D]">
            Los datos económicos están disponibles en los registros de loncheados que tienen control de rentabilidad asociado.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── UPCOMING TRABAJOS BLOCK ──────────────────────────────────────────────────
const ProximosTrabajos = ({ trabajos, loadingTrabajos, clienteId, navigate }) => {
  if (loadingTrabajos) {
    return (
      <div className="flex justify-center py-6">
        <div className="w-6 h-6 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
      </div>
    );
  }

  const ESTADO_COLORS = {
    PENDIENTE: '#C9A24D', RECIBIDO: '#8097D6', EN_PROCESO: '#D6EF4E',
    COMPLETADO: '#3A7F6B', CANCELADO: '#E05252', INCIDENCIA: '#F97316',
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-oranienbaum text-sm font-semibold text-white/70 flex items-center gap-2">
          <Icon name="Calendar" size={15} color="#D6EF4E" />
          Próximos trabajos planificados
        </h3>
        <button
          onClick={() => navigate('/plan-trabajo')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D6EF4E]/10 border border-[#D6EF4E]/20 rounded-lg text-xs font-oranienbaum text-[#D6EF4E] hover:bg-[#D6EF4E]/20 transition-colors"
        >
          <Icon name="ExternalLink" size={12} />
          Ver en Plan de Trabajo
        </button>
      </div>

      {!trabajos?.length ? (
        <p className="font-oranienbaum text-sm text-white/35 italic">No hay trabajos planificados próximos para este cliente.</p>
      ) : (
        <div className="space-y-2">
          {trabajos?.map(t => {
            const estadoColor = ESTADO_COLORS?.[t?.estado] || '#6B7280';
            return (
              <div key={t?.id} className="flex items-center gap-3 p-3 bg-[#141414] rounded-lg border border-white/8">
                <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: estadoColor }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-oranienbaum text-sm text-white/80 font-medium">
                      {t?.fecha ? new Date(t.fecha + 'T00:00:00')?.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}
                    </span>
                    <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full" style={{ background: `${estadoColor}20`, color: estadoColor }}>
                      {t?.estado}
                    </span>
                  </div>
                  <p className="font-oranienbaum text-xs text-white/50 mt-0.5">
                    {t?.producto?.nombre || 'Sin producto'} · {t?.cantidad_piezas} pieza{t?.cantidad_piezas !== 1 ? 's' : ''}
                    {t?.empleado?.nombre ? ` · ${t?.empleado?.nombre}` : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => searchParams?.get('tab') || 'general');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  const [loncheados, setLoncheados] = useState([]);
  const [rentLoncheados, setRentLoncheados] = useState([]);
  const [proximosTrabajos, setProximosTrabajos] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRent, setLoadingRent] = useState(false);
  const [loadingTrabajos, setLoadingTrabajos] = useState(false);
  const [rentFilter, setRentFilter] = useState('todo');

  const isNew = id === 'nuevo';

  useEffect(() => {
    if (isNew) {
      setClient(null);
      setFormData({ activo: true, clienteSalaCorte: false, clienteDistribucion: false });
      setEditing(true);
      setLoading(false);
    } else {
      loadClient();
    }
  }, [id]);

  useEffect(() => {
    if (!client?.id) return;
    if (activeTab === 'historial' && !loncheados?.length && !loadingHistory) loadHistory();
    if (activeTab === 'rentabilidad' && !rentLoncheados?.length && !loadingRent) loadRentabilidad();
    if (activeTab === 'condiciones_corte' && !proximosTrabajos?.length && !loadingTrabajos) loadProximosTrabajos();
  }, [activeTab, client?.id]);

  useEffect(() => {
    if (activeTab === 'rentabilidad' && client?.id) loadRentabilidad();
  }, [rentFilter]);

  const loadClient = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await unifiedClientService?.getById(id);
      setClient(data);
      setFormData(data || {});
    } catch (err) {
      setError(err?.message || 'Error al cargar el cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await unifiedClientService?.getLoncheadosHistory(client?.id);
      setLoncheados(data);
    } catch { setLoncheados([]); } finally { setLoadingHistory(false); }
  };

  const getDateRangeForFilter = (filter) => {
    const now = new Date();
    if (filter === 'semana') {
      const day = now?.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday?.setDate(now?.getDate() + diff);
      monday?.setHours(0, 0, 0, 0);
      return { fechaDesde: monday?.toISOString()?.split('T')?.[0] };
    }
    if (filter === 'mes') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { fechaDesde: first?.toISOString()?.split('T')?.[0] };
    }
    if (filter === 'año') return { fechaDesde: `${now?.getFullYear()}-01-01` };
    return {};
  };

  const loadRentabilidad = async () => {
    try {
      setLoadingRent(true);
      const filters = getDateRangeForFilter(rentFilter);
      const data = await unifiedClientService?.getRentabilidadByCliente(client?.id, filters);
      setRentLoncheados(data);
    } catch { setRentLoncheados([]); } finally { setLoadingRent(false); }
  };

  const loadProximosTrabajos = async () => {
    try {
      setLoadingTrabajos(true);
      const data = await unifiedClientService?.getProximosTrabajos(client?.id);
      setProximosTrabajos(data);
    } catch { setProximosTrabajos([]); } finally { setLoadingTrabajos(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!formData?.nombre?.trim()) {
      setError('El nombre del cliente es obligatorio');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (isNew) {
        const created = await unifiedClientService?.create(formData);
        navigate(`/clientes/${created?.id}`, { replace: true });
      } else {
        const updated = await unifiedClientService?.update(id, formData);
        setClient(updated);
        setFormData(updated);
        setEditing(false);
      }
    } catch (err) {
      setError(err?.message || 'Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isNew) navigate('/clientes');
    else { setFormData(client || {}); setEditing(false); setError(null); }
  };

  const handleToggleActive = async () => {
    try {
      const updated = await unifiedClientService?.toggleActive(client?.id, !client?.activo);
      setClient(updated);
      setFormData(updated);
    } catch (err) {
      setError(err?.message || 'Error al cambiar estado');
    }
  };

  if (loading) {
    return (
      <ERPPage>
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
        </div>
      </ERPPage>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isNew ? 'Nuevo Cliente' : (client?.nombre || 'Cliente')} — Las Dos Doncellas</title>
      </Helmet>
      <ERPPage>
        <ERPPageHeader
          title={isNew ? 'Nuevo Cliente' : (client?.nombre || 'Ficha de Cliente')}
          subtitle={
            !isNew && client?.categoria && (
              <span className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-oranienbaum font-semibold bg-white/8 text-white/50 border border-white/10">
                  {client?.categoria}
                </span>
              </span>
            )
          }
          actions={
            <div className="flex items-center gap-2">
              <ERPButton variant="ghost" icon="ArrowLeft" onClick={() => navigate('/clientes')} size="sm" btnLoading={false} disabled={false}>
                Volver
              </ERPButton>
              {!isNew && !editing && (
                <>
                  <ERPButton variant="ghost" icon={client?.activo ? 'UserX' : 'UserCheck'} onClick={handleToggleActive} size="sm" btnLoading={false} disabled={false}>
                    {client?.activo ? 'Desactivar' : 'Activar'}
                  </ERPButton>
                  <ERPButton variant="primary" icon="Edit" onClick={() => setEditing(true)} size="sm" btnLoading={false} disabled={false}>
                    Editar
                  </ERPButton>
                </>
              )}
              {editing && (
                <>
                  <ERPButton variant="ghost" icon="X" onClick={handleCancel} size="sm" disabled={saving} btnLoading={false}>
                    Cancelar
                  </ERPButton>
                  <ERPButton variant="primary" icon="Save" onClick={handleSave} size="sm" disabled={saving} btnLoading={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </ERPButton>
                </>
              )}
            </div>
          }
        />

        {error && (
          <div className="mb-4 p-3 bg-[#E05252]/10 border border-[#E05252]/30 rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={16} color="#E05252" />
            <span className="font-oranienbaum text-sm text-[#E05252]">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-[#E05252]/60 hover:text-[#E05252]">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
          {/* Tab Bar */}
          <div className="border-b border-white/8 bg-[#141414] overflow-x-auto">
            <div className="flex min-w-max">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-oranienbaum font-medium transition-colors whitespace-nowrap border-b-2 ${
                    activeTab === tab?.id
                      ? 'text-[#D6EF4E] border-[#D6EF4E] bg-[#D6EF4E]/5'
                      : 'text-white/45 border-transparent hover:text-white/70 hover:bg-white/3'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'general' && (
              <TabGeneral client={client} editing={editing} formData={formData} onChange={handleChange} />
            )}

            {activeTab === 'condiciones_corte' && !isNew && client?.id && (
              <div>
                <div className="mb-5 p-4 rounded-xl border border-[#D6EF4E]/15 bg-[#D6EF4E]/5 flex items-start gap-3">
                  <Icon name="Settings" size={18} color="#D6EF4E" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-oranienbaum font-semibold text-[#D6EF4E] text-sm">Condiciones de Corte / Planificación</p>
                    <p className="font-oranienbaum text-white/50 text-xs mt-0.5">
                      Configura los valores habituales de este cliente. Se heredarán automáticamente al crear nuevos trabajos en el Plan de Trabajo.
                    </p>
                  </div>
                </div>
                <CondicionesCortePanel clienteId={client?.id} onSaved={() => loadProximosTrabajos()} />
                <ProximosTrabajos
                  trabajos={proximosTrabajos}
                  loadingTrabajos={loadingTrabajos}
                  clienteId={client?.id}
                  navigate={navigate}
                />
              </div>
            )}

            {activeTab === 'condiciones_corte' && isNew && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="Settings" size={32} color="#D6EF4E" />
                <p className="font-oranienbaum text-white/50 text-sm mt-3">Guarda primero el cliente para configurar sus condiciones de corte.</p>
              </div>
            )}

            {activeTab === 'historial' && (
              <TabHistorial loncheados={loncheados} loadingHistory={loadingHistory} />
            )}

            {activeTab === 'rentabilidad' && (
              <TabRentabilidad
                loncheados={rentLoncheados}
                loadingRent={loadingRent}
                rentFilter={rentFilter}
                setRentFilter={(f) => setRentFilter(f)}
              />
            )}
          </div>
        </div>
      </ERPPage>
    </>
  );
};

export default ClientDetail;
